import React, { FC, useContext, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDeepCompareEffect } from '@oscar/utils';
import { validateRequest } from '@oscar/request';
import { FieldProps } from './Field';
import CustomField from './CustomField';
import { submitData } from '../reducer';
import {
  getCommitedByKeySelector,
  getLoadingByKeySelector,
} from '../selectors';
import { ActionFieldProps, CustomFieldProps } from '../typed';
import { FormContext } from '../formContext';

interface AsyncProps extends CustomFieldProps, ActionFieldProps {}

const ActionField: FC<AsyncProps> = ({ children, ...props }) => {
  const { request, field, onResponseData } = props;
  const context = useContext(FormContext);
  const dispatch = useDispatch();
  const getCommitedByKey = getCommitedByKeySelector(context.options!.name);
  const responseData = useSelector((state) =>
    getCommitedByKey(state, `${props.name}.${request.name}`)
  );
  const getLoadingByKey = getLoadingByKeySelector(context.options!.name);
  const isLoading = useSelector((state) =>
    getLoadingByKey(state, `${props.name}.${request.name}`)
  );

  const getFormValues = useCallback(() => {
    return {
      ...context.selectors?.getInitalValue(),
      ...field?.getFieldsValue(),
    };
  }, [context, field]);

  useDeepCompareEffect(() => {
    if (responseData && onResponseData) {
      onResponseData(responseData, field!);
    }
  }, [responseData]);

  const submitDataHandler = useCallback(
    async (data, skipValidate) => {
      try {
        if (!skipValidate) {
          await field?.validateFields();
        }

        const submitRequest = validateRequest<AsyncProps>(
          {
            ...request,
            data: data ? data : field?.getFieldsValue(),
            name: `${props.name}.${request.name}`,
          },
          props
        );
        if (submitRequest.shouldRequest) {
          dispatch(
            submitData.action({
              metadata: {
                form: context.options!.name,
              },
              request: submitRequest,
            })
          );
        }
      } catch {}
    },
    [request]
  );

  return typeof children === 'function'
    ? children({
        ...props,
        responseData,
        isLoading,
        submitData: submitDataHandler,
        getFormValues,
      })
    : null;
};

export default (props: FieldProps & ActionFieldProps) => (
  <CustomField<ActionFieldProps> {...props} component={ActionField} />
);
